import { ChangeEvent, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { MAX_FILE_SIZE } from './const'
import { Button } from '@/Atomic/Molecules/Button'
import { getFileExtension } from '@/Atomic/Molecules/FileIcon/utils'
import { showUploadToast } from '@/Components/Toast/UploadToast'
import { DocumentsUploadOptions } from '@/hooks/documents/uploadDocuments'
import { useDocumentContext } from '@/hooks/documents/useDocumentHandler'

export type DocumentsUploadFn = (
  files: File[],
  opts?: DocumentsUploadOptions,
) => Promise<void>

export interface DocumentsUploadButtonProps {
  onUpload: DocumentsUploadFn
  allowNoRelation?: boolean
}

export const DocumentsUploadButton: React.FC<DocumentsUploadButtonProps> = ({
  onUpload,
  allowNoRelation,
}) => {
  const inputFileRef = useRef<HTMLInputElement>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [tooLargeError, setTooLargeError] = useState('')
  const [uploadError, setUploadError] = useState('')
  const { t } = useTranslation()
  const singleFileTimerRef = useRef<number | null>(null)

  const startUpload = async (files: File[]) => {
    if (!files.length) return
    setIsSubmitting(true)
    const total = files.length
    setUploadError('')

    const title = total === 1 ? files[0]!.name : `Uploading ${total} files`

    const toastCtrl = showUploadToast({
      title,
      percent: 0,
      extension: total === 1 ? getFileExtension(files[0]!.name) : undefined,
    })

    // For a single file we can't get real progress; simulate to 90%
    if (total === 1) {
      let simulated = 0
      const step = () => {
        singleFileTimerRef.current = window.setTimeout(() => {
          // Increase by small step up to 90%
          simulated = Math.min(
            90,
            simulated + Math.round(5 + Math.random() * 8),
          )
          toastCtrl.update({ percent: simulated })
          if (simulated < 90) step()
        }, 500)
      }
      step()
    }

    try {
      await onUpload(files, {
        allowNoRelation,
        onProgress: (completed) => {
          if (total > 1) {
            const pct = Math.round((completed / total) * 100)
            toastCtrl.update({ percent: pct })
          }
        },
        onError: (e) => {
          const message = formatErrorMessage(e)
          setUploadError(message)
        },
      })
      // Ensure 100% on completion
      toastCtrl.update({ percent: 100 })
      // Clear simulated timer
      if (singleFileTimerRef.current) {
        clearTimeout(singleFileTimerRef.current)
        singleFileTimerRef.current = null
      }
      // Close after a short delay
      setTimeout(() => toastCtrl.close(), 200)
      handleReset()
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatErrorMessage = (e: unknown) => {
    if (e instanceof Error) return e.message
    if (typeof e === 'string') return e
    try {
      return JSON.stringify(e)
    } catch {
      return 'Unknown error'
    }
  }

  const uploadFile = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return

    const filesArray = Array.from(e.target.files)
    const validFiles = filesArray.filter((f) => f.size <= MAX_FILE_SIZE)
    const rejected = filesArray.length - validFiles.length

    if (rejected > 0) {
      setTooLargeError(
        rejected === 1
          ? t('components.message.errors.attachmentTooLargeSingle')
          : t('components.message.errors.attachmentsTooLargeMultiple', {
              count: rejected,
            }),
      )
    } else {
      setTooLargeError('')
    }

    if (validFiles.length) {
      // Start upload immediately with the chosen files
      startUpload(validFiles)
    }
  }

  const handleReset = () => {
    if (inputFileRef.current) {
      inputFileRef.current.value = ''
    }
  }

  const openFilePicker = () => inputFileRef.current?.click()

  return (
    <>
      <input
        type="file"
        name="attachment"
        onChange={uploadFile}
        ref={inputFileRef}
        multiple
        style={{ display: 'none' }}
      />

      <Button
        title={t('components.document.upload')}
        icon="upload"
        onClick={openFilePicker}
        variant="primary"
        disabled={isSubmitting}
        size="sm"
        className="ms-2"
      />
      {tooLargeError && (
        <div className="invalid-feedback d-block">{tooLargeError}</div>
      )}
      {uploadError && (
        <div className="invalid-feedback d-block">{uploadError}</div>
      )}
    </>
  )
}

export const DocumentsUploadTool: React.FC = () => {
  const { uploadDocuments } = useDocumentContext()
  return <DocumentsUploadButton onUpload={uploadDocuments} />
}
